import { useState, type FormEvent } from 'react';
import { useLookups, normalizeUnitLabel } from '../lib/lookups';
import { useUpdateStockTransfer, type StockTransferRow } from '../lib/finance';
import { useProjectStock } from '../lib/stock-transfer-batches';
import { Drawer, Field, useToast, Select } from './ui';
import { toPaise, formatMoney } from '@vns/core';
import { Repeat, Truck, Calendar, MapPin, Box, IndianRupee } from 'lucide-react';

const cleanMoney = (s: string) => s.replace(/[₹,\s]/g, '');

/**
 * Single-line edit drawer for correcting a recorded stock transfer entry.
 */
export default function EditStockTransferDrawer({
  existing,
  onClose,
  onDelete,
}: {
  existing: StockTransferRow;
  onClose: () => void;
  onDelete: () => void;
}) {
  const { data: lk, isLoading } = useLookups();
  const update = useUpdateStockTransfer();
  const toast = useToast();

  const [form, setForm] = useState({
    date: existing.date,
    from_project_id: existing.fromProjectId,
    to_project_id: existing.toProjectId,
    material_id: existing.materialId,
    unit_id: existing.unitId ?? '',
    quantity: String(existing.quantity),
    rateRupees: existing.ratePaise != null ? String(existing.ratePaise / 100) : '',
    vehicle_no: existing.vehicleNo ?? '',
    driver_name: existing.driverName ?? '',
    remarks: existing.remarks ?? '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: stockMap, isLoading: stockLoading } = useProjectStock(form.from_project_id);

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const toOptions = lk?.projects.filter((p) => p.id !== form.from_project_id);
  const fromOptions = lk?.projects.filter((p) => p.id !== form.to_project_id);

  const numQty = parseFloat(form.quantity) || 0;
  const numRate = parseFloat(form.rateRupees) || 0;
  const totalPaise = numQty > 0 && numRate > 0 ? Math.round(numQty * toPaise(form.rateRupees)) : 0;

  // When editing, available stock at source site = current site stock + previously recorded quantity on this entry (if same site & material)
  const isOriginalEntry = existing.fromProjectId === form.from_project_id && existing.materialId === form.material_id;
  const availableStock = (stockMap?.get(form.material_id) ?? 0) + (isOriginalEntry ? existing.quantity : 0);

  function validate() {
    const e: Record<string, string> = {};
    if (!form.from_project_id) e.from_project_id = 'Select the sending site';
    if (!form.to_project_id) e.to_project_id = 'Select the receiving site';
    else if (form.to_project_id === form.from_project_id) e.to_project_id = 'Choose a different site';
    if (!form.material_id) e.material_id = 'Select a material';
    if (!form.quantity || Number(form.quantity) <= 0) e.quantity = 'Enter a quantity';
    else if (form.material_id && stockMap && Number(form.quantity) > availableStock) {
      e.quantity = availableStock > 0 ? `Exceeds available stock (Max: ${availableStock})` : 'No stock available on site (0)';
    }
    return e;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length) return;

    try {
      await update.mutateAsync({ id: existing.id, ...form });
      toast.show('Transfer updated');
      onClose();
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not save the transfer', true);
    }
  }

  return (
    <Drawer
      title="Edit Stock Transfer"
      description={`Correcting transfer record for ${existing.materialName}`}
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel="Save Changes"
      busy={update.isPending}
      onDelete={onDelete}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
        <Field label="Transfer Date">
          <input
            type="date"
            value={form.date}
            onChange={set('date')}
            className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
          />
        </Field>

        <Field
          label="Material"
          error={errors.material_id}
          hint={
            form.material_id && form.from_project_id
              ? stockLoading
                ? 'Checking stock…'
                : availableStock > 0
                ? `Available on site: ${availableStock}`
                : 'Out of stock on site'
              : undefined
          }
        >
          <Select
            value={form.material_id}
            onChange={(val) => set('material_id')({ target: { value: val } })}
            disabled={isLoading}
            placeholder={isLoading ? 'Loading…' : 'Choose a material'}
            options={[
              { value: '', label: isLoading ? 'Loading…' : 'Choose a material' },
              ...(lk?.materials?.map((m) => {
                const isOrig = existing.fromProjectId === form.from_project_id && existing.materialId === m.id;
                const avail = form.from_project_id && stockMap ? (stockMap.get(m.id) ?? 0) + (isOrig ? existing.quantity : 0) : null;
                const u = lk?.units.find((unit) => unit.id === m.unit_id)?.short_name ?? '';
                const stockSuffix = avail !== null ? ` (${avail} ${u} in stock)` : '';
                return { value: m.id, label: `${m.name}${stockSuffix}` };
              }) ?? []),
            ]}
            className={errors.material_id ? 'border-rose-500' : ''}
          />
        </Field>

        <Field label="Sending Site (From)" error={errors.from_project_id}>
          <Select
            value={form.from_project_id}
            onChange={(val) => set('from_project_id')({ target: { value: val } })}
            disabled={isLoading}
            placeholder={isLoading ? 'Loading…' : 'Choose sending site'}
            options={[
              { value: '', label: isLoading ? 'Loading…' : 'Choose sending site' },
              ...(fromOptions?.map((p) => ({ value: p.id, label: p.name })) ?? []),
            ]}
            className={errors.from_project_id ? 'border-rose-500' : ''}
          />
        </Field>

        <Field label="Receiving Site (To)" error={errors.to_project_id}>
          <Select
            value={form.to_project_id}
            onChange={(val) => set('to_project_id')({ target: { value: val } })}
            disabled={isLoading}
            placeholder={isLoading ? 'Loading…' : 'Choose receiving site'}
            options={[
              { value: '', label: isLoading ? 'Loading…' : 'Choose receiving site' },
              ...(toOptions?.map((p) => ({ value: p.id, label: p.name })) ?? []),
            ]}
            className={errors.to_project_id ? 'border-rose-500' : ''}
          />
        </Field>

        <Field label="Quantity" error={errors.quantity}>
          <input
            value={form.quantity}
            onChange={set('quantity')}
            inputMode="decimal"
            aria-invalid={!!errors.quantity}
            className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800 font-mono font-semibold"
          />
        </Field>

        <Field label="Unit of Measurement" optional>
          <Select
            value={form.unit_id}
            onChange={(val) => set('unit_id')({ target: { value: val } })}
            disabled={isLoading}
            placeholder="Choose a unit"
            options={[
              { value: '', label: 'Choose a unit' },
              ...(lk?.units?.map((u) => ({ value: u.id, label: normalizeUnitLabel(u.name, u.short_name) })) ?? []),
            ]}
          />
        </Field>

        <Field label="Rate (₹ / unit)" optional hint="Market rate per unit">
          <input
            value={form.rateRupees}
            onChange={(e) => setForm((f) => ({ ...f, rateRupees: cleanMoney(e.target.value) }))}
            inputMode="decimal"
            className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800 font-mono"
          />
        </Field>

        <Field label="Total Transfer Value (₹)" optional hint="Auto-calculated (Qty × Rate)">
          <div className="w-full px-3 py-2 text-xs sm:text-sm bg-emerald-50/50 border border-emerald-200/80 rounded-[8px] text-emerald-800 font-mono font-semibold flex items-center justify-between">
            <span>{totalPaise > 0 ? formatMoney(totalPaise) : '₹0'}</span>
            <span className="text-[11px] font-normal text-emerald-600">
              {numQty > 0 && numRate > 0 ? `${numQty} × ₹${form.rateRupees}` : 'Enter rate'}
            </span>
          </div>
        </Field>

        <Field label="Vehicle No." optional>
          <input
            value={form.vehicle_no}
            onChange={set('vehicle_no')}
            className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800 font-mono"
          />
        </Field>

        <Field label="Driver Name" optional>
          <input
            value={form.driver_name}
            onChange={set('driver_name')}
            className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
          />
        </Field>

        <div className="col-span-1 sm:col-span-2">
          <Field label="Remarks" optional span2>
            <textarea
              rows={2}
              value={form.remarks}
              onChange={set('remarks')}
              className="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50/50 border border-slate-200/80 rounded-[8px] focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-slate-800"
            />
          </Field>
        </div>
      </div>
    </Drawer>
  );
}
